import {
  CheckIcon,
  DeleteIcon,
  GKButton,
  GKModal,
  GKTextArea,
  GKTextField,
  Grid2,
  LinkIcon,
  PopConfirm,
  Show,
  Typography,
} from "components";
import GKImageUploader from "components/GKImageUploader";
import { isEmpty } from "lodash";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import {
  useCreateTestimonialMutation,
  useDeleteTestimonialMutation,
  useUpdateTestimonialMutation,
} from "store/features/MyWebsite";
import { handleFreshReturnFormData } from "utils";
import { GKModalProps } from "utils/commonInterface";
import { validateNotWhitespaceOnly } from "utils/validationSchema";
import { Testimonial } from "./types";

const TestimonialFormModal = ({
  open,
  setOpen,
  testimonialDetails = null,
}: GKModalProps & { testimonialDetails?: Testimonial }) => {
  const [croppedClientImage, setCroppedClientImage] = useState<string | null>(
    testimonialDetails?.clientsImage || null,
  );
  const [selectedClientImage, setSelectedClientImage] = useState<string | null>(
    null,
  );
  const [confirmationModal, setConfirmationModal] =
    useState<HTMLElement | null>(null);

  const [createTestimonial, { isLoading }] = useCreateTestimonialMutation();
  const [updateTestimonial, { isLoading: updateTestimonialLoading }] =
    useUpdateTestimonialMutation();
  const [deleteTestimonial] = useDeleteTestimonialMutation();

  const form = useForm({
    defaultValues: {
      clientsImage: testimonialDetails?.clientsImage || "",
      name: testimonialDetails?.name || "",
      twitterUsername: testimonialDetails?.twitterUsername || "",
      twitterLink: testimonialDetails?.twitterLink || "",
      clientsReview: testimonialDetails?.clientsReview || "",
    },
  });

  const handlePhotoDelete = async (field: string) => {
    const formData = new FormData();
    formData.append(field, "");
    if (!testimonialDetails?.rowId) return;
    await updateTestimonial({
      id: testimonialDetails?.rowId,
      body: formData,
    }).unwrap();

    setOpen(false);
  };

  return (
    <>
      <GKModal
        modalTitle={
          testimonialDetails?.rowId ? "Edit Testimonial" : "Add New Testimonial"
        }
        width={"466px"}
        open={open}
        setOpen={setOpen}
        maxHeight="calc(100vh - 292px)"
        footer={
          <Grid2 size={12} container justifyContent={"space-between"}>
            <GKButton
              variant={"outlined"}
              color={testimonialDetails?.rowId ? "error" : "dark"}
              startIcon={testimonialDetails?.rowId && <DeleteIcon />}
              onClick={(event) => {
                if (testimonialDetails?.rowId) {
                  setConfirmationModal(event.currentTarget);
                } else {
                  setOpen(false);
                }
              }}
            >
              {testimonialDetails?.rowId ? "Delete" : "Cancel"}
            </GKButton>
            <GKButton
              variant="contained"
              startIcon={<CheckIcon />}
              type="submit"
              form="testimonial-form"
              loading={isLoading || updateTestimonialLoading}
            >
              {testimonialDetails?.rowId ? "Save Changes" : "Add Testimonial"}
            </GKButton>
          </Grid2>
        }
      >
        <form
          id="testimonial-form"
          onSubmit={form.handleSubmit(async (values) => {
            let newObject = { ...values };
            if (
              isEmpty(newObject?.clientsImage) ||
              typeof newObject?.clientsImage === "string"
            ) {
              delete newObject.clientsImage;
            } else {
              newObject = {
                ...newObject,
                clientsImage: values.clientsImage[0],
              };
            }

            if (testimonialDetails?.rowId) {
              try {
                await updateTestimonial({
                  id: testimonialDetails?.rowId,
                  body: handleFreshReturnFormData({
                    isEditForm: false,
                    obj: { ...newObject },
                  }),
                }).unwrap();
                setOpen(false);
              } catch (error) {
                console.error("Error updating testimonial:", error);
              }
            } else {
              try {
                await createTestimonial(
                  handleFreshReturnFormData({
                    isEditForm: false,
                    obj: { ...newObject },
                  }),
                ).unwrap();
                setOpen(false);
              } catch (error) {
                console.error("Error creating testimonial:", error);
              }
            }
          })}
        >
          <Grid2 container spacing={3}>
            <Grid2 size={12} container spacing={1}>
              <Grid2 size={12}>
                <Typography
                  fontSize={13}
                  fontWeight={500}
                  color="var(--grey-900)"
                >
                  Upload Client’s Image
                </Typography>
              </Grid2>
              <Grid2 size={12}>
                <Controller
                  name="clientsImage"
                  control={form.control}
                  render={() => (
                    <GKImageUploader
                      croppedImage={croppedClientImage}
                      handleProfilePicDelete={() =>
                        handlePhotoDelete("clientsImage")
                      }
                      selectedImage={selectedClientImage}
                      setCroppedImage={setCroppedClientImage}
                      setSelectedImage={setSelectedClientImage}
                      form={form}
                      logoName={"clientsImage"}
                      buttonName="Upload Image"
                    />
                  )}
                />
              </Grid2>
            </Grid2>
            <Grid2 size={6}>
              <Controller
                name="name"
                control={form.control}
                rules={{
                  required: {
                    value: true,
                    message: "Please enter full name",
                  },
                  validate: validateNotWhitespaceOnly,
                }}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <GKTextField
                      {...field}
                      inputLabel="Full Name"
                      requiredField
                      error={!!error}
                      helperText={error?.message}
                    />
                  );
                }}
              />
            </Grid2>
            <Grid2 size={6}>
              <Controller
                name="twitterUsername"
                control={form.control}
                rules={{
                  pattern: {
                    value: /^@.+$/,
                    message: "Twitter username must start with @",
                  },
                }}
                render={({ field, fieldState: { error } }) => {
                  return (
                    <GKTextField
                      {...field}
                      inputLabel="X(Twitter) Username"
                      slotProps={{ input: { endAdornment: "@" } }}
                      error={!!error}
                      helperText={error?.message}
                    />
                  );
                }}
              />
            </Grid2>
            <Grid2 size={12}>
              <Controller
                name="twitterLink"
                control={form.control}
                render={({ field }) => {
                  return (
                    <GKTextField
                      {...field}
                      inputLabel="X(Twitter) Link"
                      slotProps={{
                        input: {
                          endAdornment: <LinkIcon width={16} height={16} />,
                        },
                      }}
                    />
                  );
                }}
              />
            </Grid2>
            <Grid2 size={12}>
              <Controller
                name="clientsReview"
                control={form.control}
                render={({ field }) => (
                  <GKTextArea {...field} inputLabel="Client’s Review" />
                )}
              />
            </Grid2>
          </Grid2>
        </form>
      </GKModal>

      <Show.When isTrue={Boolean(confirmationModal)}>
        <PopConfirm
          anchorEl={null}
          open={Boolean(confirmationModal)}
          setOpen={setConfirmationModal}
          modalTitle="Delete Testimonial?"
          message="Are you sure you want to delete this Testimonial? This action cannot be undone and will remove the question from your website."
          transformOrigin={{ vertical: "center", horizontal: "center" }}
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
          onSuccess={async () => {
            try {
              await deleteTestimonial(testimonialDetails?.rowId).unwrap();
              setOpen(false);
            } catch (error) {
              console.error("Error deleting testimonial:", error);
            }
          }}
        />
      </Show.When>
    </>
  );
};

export default TestimonialFormModal;
